import { Suspense } from 'react';

import { Navigate, Route, Routes } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import { selectIsSuperUser } from '@/store/slices/userSlice';

import { LoadingIndicator } from '@/components/common/Loading';

import { useLocalStorageState } from '@/hooks/useLocalStorageState';
import { RoutePath } from '@/utils/enums';
import { STORAGE_KEYS } from '@/utils/storageKeys';

import { routes } from './config';
import {
  AdminGuard,
  AdvisorAdminGuard,
  AdvisorGuard,
  PlatformAdminGuard,
  SuperUserGuard,
} from './guards';

/** Top-level admin prefixes that require super-user access. */
const SUPER_USER_PREFIXES = [
  RoutePath.ADMIN_USERS,
  RoutePath.ADMIN_AUDIT,
  RoutePath.ADMIN_BLOCK_TRADE,
  RoutePath.ADMIN_KEY_METRICS,
];

const isSuperUserPath = (path: string) =>
  SUPER_USER_PREFIXES.some(prefix => path.startsWith(prefix));

function AdminIndexRedirect() {
  const isSuperUser = useAppSelector(selectIsSuperUser);
  const defaultRoute = isSuperUser
    ? RoutePath.ADMIN_USERS
    : RoutePath.ADMIN_COMPANIES;

  const [lastSection] = useLocalStorageState<string>(
    STORAGE_KEYS.ADMIN_LAST_SECTION,
    ''
  );

  // Use persisted route only if it's valid for the user's role
  const target =
    lastSection && (!isSuperUserPath(lastSection) || isSuperUser)
      ? lastSection
      : defaultRoute;

  return <Navigate to={target} replace />;
}

export const AppRoutes = () => {
  return (
    <Suspense fallback={<LoadingIndicator />}>
      <Routes>
        <Route element={<AdvisorGuard />}>
          <Route element={<AdvisorAdminGuard />}>
            {routes.protected.map(({ path, element: Element }) => (
              <Route key={path} path={path} element={<Element />} />
            ))}
          </Route>

          <Route element={<AdminGuard />}>
            <Route path={RoutePath.ADMIN} element={<routes.admin.layout />}>
              <Route index element={<AdminIndexRedirect />} />
              {routes.admin.routes.map(({ path, element: Element }) => (
                <Route key={path} path={path} element={<Element />} />
              ))}
              {routes.superAdmin.map(({ path, element: Element }) => (
                <Route key={path} element={<SuperUserGuard />}>
                  <Route path={path} element={<Element />} />
                </Route>
              ))}
              {routes.platformAdmin.map(({ path, element: Element }) => (
                <Route key={path} element={<PlatformAdminGuard />}>
                  <Route path={path} element={<Element />} />
                </Route>
              ))}
            </Route>
          </Route>
        </Route>

        <Route path={RoutePath.SETTINGS} element={<routes.settings.layout />}>
          <Route
            index
            element={<Navigate to={RoutePath.SETTINGS_ACCOUNT} replace />}
          />
          {routes.settings.routes.map(({ path, element: Element }) => (
            <Route key={path} path={path} element={<Element />} />
          ))}
        </Route>

        <Route
          path="*"
          element={<Navigate to={RoutePath.DASHBOARD} replace />}
        />
      </Routes>
    </Suspense>
  );
};
